import { useMemo, useRef, useState } from 'react';
import { AlertTriangle, Receipt, Undo2, XCircle } from 'lucide-react';

import { Button } from '@/components/Button';
import { Segmented } from '@/components/Segmented';
import { ClientMappingPanel } from './ClientMappingPanel';
import { formatDuration, sumMoney } from './money';
import { InvoiceHistory } from './InvoiceHistory';
import { ProjectMappingPanel } from './ProjectMappingPanel';
import {
  useClientMap,
  useDismissTasks,
  useDismissed,
  useGenerateInvoice,
  useItflowSettings,
  useReadyToBill,
  useUndismissTasks,
} from './useItflow';
import type { RateSource, ReadyRow } from './types';

type Tab = 'ready' | 'dismissed' | 'invoices' | 'mapping';

const TABS: { value: Tab; label: string }[] = [
  { value: 'ready', label: 'Ready' },
  { value: 'dismissed', label: 'Never' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'mapping', label: 'Mapping' },
];

const RATE_LABELS: Record<RateSource, string> = {
  non_billable: 'not billable',
  snapshot: 'rate when logged',
  project: 'project rate',
  client: 'client rate',
  itflow: 'ITFlow rate',
  default: 'default rate',
};

/**
 * Finished work, on its way to an invoice.
 *
 * The list only offers the shallowest completed task in any subtree, because a
 * parent's logged time already contains its children's, offering both would
 * invite billing the same hour twice. Anything you are not going to charge for
 * gets marked never-bill and leaves for good, which is what keeps the list
 * worth opening.
 */
export function BillingPage() {
  const settings = useItflowSettings();
  const configured = Boolean(settings.data?.configured);

  const [tab, setTab] = useState<Tab>('ready');
  const [clientId, setClientId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [scope, setScope] = useState('');

  const clients = useClientMap(configured);
  const ready = useReadyToBill(clientId, configured);
  const dismissed = useDismissed(configured && tab === 'dismissed');
  const dismiss = useDismissTasks();
  const undismiss = useUndismissTasks();
  const generate = useGenerateInvoice();

  // Minted once per press and reused on every retry of that press. This is
  // what makes a double-click one invoice rather than two.
  const requestKey = useRef<string | null>(null);

  const rows = useMemo(() => ready.data ?? [], [ready.data]);
  const chosen = useMemo(() => rows.filter((r) => selected.has(r.task_id)), [rows, selected]);

  // One invoice cannot span two clients, or two currencies.
  const chosenClients = new Set(chosen.map((r) => r.client_id));
  const chosenCurrencies = new Set(chosen.map((r) => r.currency));
  const blocked =
    chosenClients.size > 1
      ? 'Selected tasks belong to different clients, invoice one client at a time.'
      : chosenCurrencies.size > 1
        ? 'Selected tasks use different currencies, they need separate invoices.'
        : null;

  const total = sumMoney(chosen.map((r) => r.amount));

  const toggle = (taskId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const submit = () => {
    const target = chosen[0];
    if (!target) return;
    requestKey.current ??= crypto.randomUUID().replace(/-/g, '');
    generate.mutate(
      {
        client_id: target.client_id,
        task_ids: chosen.map((r) => r.task_id),
        request_key: requestKey.current,
        scope: scope.trim() || undefined,
      },
      {
        onSuccess: () => {
          setSelected(new Set());
          setScope('');
          requestKey.current = null;
          setTab('invoices');
        },
      },
    );
  };

  if (!configured) {
    return (
      <div className="mx-auto flex max-w-prose flex-col gap-3 px-4 py-10">
        <h1 className="text-heading font-medium text-text">Billing</h1>
        <p className="text-body text-text-secondary">
          Connect your ITFlow instance in Settings → ITFlow billing to turn completed
          work into invoices.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-6">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 text-heading font-medium text-text">
          <Receipt size={18} strokeWidth={1.5} aria-hidden />
          Billing
        </h1>
        <p className="max-w-prose text-small text-text-secondary">
          Completed work for mapped clients. Each line rounds up to the nearest{' '}
          {settings.data ? settings.data.round_to_seconds / 60 : 15} minutes.
        </p>
      </div>

      <Segmented label="View" value={tab} options={TABS} onChange={setTab} />

      {tab === 'ready' && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-2">
              <label htmlFor="billing-client" className="text-small font-medium text-text-secondary">
                Client
              </label>
              <select
                id="billing-client"
                value={clientId ?? ''}
                onChange={(e) => {
                  setClientId(e.target.value ? Number(e.target.value) : null);
                  setSelected(new Set());
                }}
                className="min-h-touch rounded border border-border bg-surface-1 px-3 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
              >
                <option value="">All mapped clients</option>
                {(clients.data?.links ?? []).map((link) => (
                  <option key={link.id} value={link.client_id}>
                    {clients.data?.chronos.find((c) => c.id === link.client_id)?.name ??
                      `Client #${link.client_id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {ready.isError && !ready.data ? (
            <p role="alert" className="text-body text-danger">
              Could not load ready-to-bill work.{' '}
              <button type="button" className="underline" onClick={() => ready.refetch()}>
                Retry
              </button>
            </p>
          ) : ready.isLoading ? (
            <div className="h-48 animate-pulse rounded-lg bg-surface-2" aria-hidden />
          ) : rows.length === 0 ? (
            <p className="text-small text-text-tertiary">
              Nothing waiting. Completed tasks with logged time show up here.
            </p>
          ) : (
            <>
              <ul className="flex flex-col gap-2">
                {rows.map((row) => (
                  <ReadyLine
                    key={row.task_id}
                    row={row}
                    checked={selected.has(row.task_id)}
                    onToggle={() => toggle(row.task_id)}
                  />
                ))}
              </ul>

              {chosen.length > 0 && (
                <div className="sticky bottom-[var(--chrome-bottom,0px)] z-sticky flex flex-col gap-3 rounded-lg border border-border bg-surface-2 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-body text-text">
                      {chosen.length} selected · {chosen[0]?.currency} {total}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          dismiss.mutate(chosen.map((r) => r.task_id));
                          setSelected(new Set());
                        }}
                      >
                        <XCircle size={14} strokeWidth={1.5} aria-hidden />
                        Never bill these
                      </Button>
                      <Button
                        variant="primary"
                        onClick={submit}
                        disabled={blocked !== null}
                        loading={generate.isPending}
                      >
                        Generate invoice
                      </Button>
                    </div>
                  </div>

                  <input
                    aria-label="Invoice scope"
                    value={scope}
                    onChange={(e) => setScope(e.target.value)}
                    placeholder="Invoice scope (optional): e.g. March support"
                    className="min-h-touch rounded border border-border bg-surface-1 px-3 text-body text-text placeholder:text-text-tertiary outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
                  />

                  {blocked && (
                    <p className="flex items-center gap-2 text-small text-warning">
                      <AlertTriangle size={14} strokeWidth={1.5} aria-hidden />
                      {blocked}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === 'dismissed' && (
        <ul className="flex flex-col gap-2">
          {(dismissed.data ?? []).map((row) => (
            <li
              key={row.task_id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-1 p-3"
            >
              <span className="truncate text-body text-text">{row.title}</span>
              <Button variant="ghost" onClick={() => undismiss.mutate([row.task_id])}>
                <Undo2 size={14} strokeWidth={1.5} aria-hidden />
                Put back
              </Button>
            </li>
          ))}
          {(dismissed.data ?? []).length === 0 && (
            <li className="text-small text-text-tertiary">
              Nothing marked never-bill.
            </li>
          )}
        </ul>
      )}

      {tab === 'invoices' && <InvoiceHistory />}

      {tab === 'mapping' && (
        <div className="flex flex-col gap-8">
          <ClientMappingPanel />
          <div className="flex flex-col gap-2">
            <h2 className="text-body font-medium text-text">Projects</h2>
            <ProjectMappingPanel />
          </div>
        </div>
      )}
    </div>
  );
}

function ReadyLine({
  row,
  checked,
  onToggle,
}: {
  row: ReadyRow;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <label className="flex cursor-pointer flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-1 p-3 hover:bg-surface-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="size-icon shrink-0 accent-[var(--accent)]"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-body text-text">{row.title}</span>
          <span className="truncate text-small text-text-tertiary">
            {row.client_name}
            {row.project_name && ` · ${row.project_name}`}
            {' · '}
            {formatDuration(row.logged_seconds)} logged
          </span>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <span className="text-body text-text">
            {row.currency} {row.amount}
          </span>
          <span className="flex items-center gap-1.5 text-small text-text-tertiary">
            {row.qty_hours}h × {row.rate}
            <span className="rounded bg-surface-3 px-1.5 py-0.5 text-label">
              {RATE_LABELS[row.rate_source]}
            </span>
            {row.mixed_rates && (
              <AlertTriangle
                size={12}
                strokeWidth={1.5}
                className="text-warning"
                aria-label="Time on this task was logged at more than one rate"
              />
            )}
          </span>
        </div>
      </label>
    </li>
  );
}
